import { useRef, useState } from "react";
import { AxiosError } from "axios";

import {
  getAttachmentDownloadUrl,
  downloadAllAttachments,
} from "@/api/attachments";
import { PennButton, PennText } from "@/components/ui";
import {
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from "@/hooks/useAttachments";

const MAX_BYTES = 50 * 1024 * 1024;

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? fallback;
  }
  return fallback;
}

function formatSize(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type AttachmentsPanelProps = {
  applicationId: string;
  /**
   * Read-only mode (issue 3 — task side panel): hides upload + per-file delete +
   * the upload helper text. Keeps the list, per-file Download and "Download all".
   */
  readOnly?: boolean;
};

export const AttachmentsPanel = ({
  applicationId,
  readOnly = false,
}: AttachmentsPanelProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listQuery = useAttachments(applicationId);
  const upload = useUploadAttachment();
  const remove = useDeleteAttachment();

  const [localError, setLocalError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file(s)
    if (files.length === 0) return;
    const tooLarge = files.filter((f) => f.size > MAX_BYTES);
    const ok = files.filter((f) => f.size <= MAX_BYTES);
    if (tooLarge.length > 0) {
      setLocalError(
        `Skipped ${tooLarge.length} file${tooLarge.length === 1 ? "" : "s"} over the 50 MB limit: ${tooLarge
          .map((f) => f.name)
          .join(", ")}`,
      );
    } else {
      setLocalError(null);
    }
    for (const file of ok) {
      upload.mutate({ applicationId, file });
    }
  };

  const onDownload = async (fileId: string) => {
    setLocalError(null);
    setDownloadingId(fileId);
    try {
      const { url } = await getAttachmentDownloadUrl(applicationId, fileId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setLocalError(errorMessage(err, "Could not start the download."));
    } finally {
      setDownloadingId(null);
    }
  };

  const onDelete = (fileId: string, fileName: string) => {
    setLocalError(null);
    if (!window.confirm(`Remove "${fileName}"?`)) return;
    remove.mutate({ applicationId, fileId });
  };

  const onDownloadAll = async () => {
    setLocalError(null);
    setZipping(true);
    try {
      await downloadAllAttachments(applicationId);
    } catch (err) {
      setLocalError(
        errorMessage(
          err,
          "Could not build the ZIP. Try downloading the files individually.",
        ),
      );
    } finally {
      setZipping(false);
    }
  };

  const items = listQuery.data ?? [];
  const errorText =
    localError ||
    (upload.isError && errorMessage(upload.error, "Upload failed.")) ||
    (remove.isError && errorMessage(remove.error, "Delete failed.")) ||
    null;

  return (
    <div className="space-y-3">
      {!readOnly && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <PennButton
              type="button"
              variant="secondary"
              size="md"
              onClick={onPick}
              disabled={upload.isPending}
            >
              {upload.isPending ? "Uploading…" : "Upload files"}
            </PennButton>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={onFile}
            />
          </div>

          <PennText as="p" size="xs" color="blue_grey">
            Attach supporting documents (drawings, specs, photos), up to
            50&nbsp;MB each.
          </PennText>
        </>
      )}

      {errorText && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {errorText}
        </div>
      )}

      {items.length === 0 ? (
        <div className="py-4 text-center border border-dashed border-gray-200 rounded-2xl px-3">
          <PennText as="p" size="sm" color="blue_grey">
            No files uploaded yet.
          </PennText>
        </div>
      ) : (
        <>
          {/* POC parity (issue 4): "Download all" sits in the files-list header,
              right-aligned opposite the "Uploaded files" label — not in the top
              upload row. */}
          <div className="flex items-center justify-between gap-2">
            <PennText
              as="span"
              size="xs"
              color="blue_grey"
              className="uppercase font-semibold"
            >
              Uploaded files
            </PennText>
            <PennButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void onDownloadAll()}
              disabled={zipping}
            >
              {zipping ? "Preparing ZIP…" : "Download all"}
            </PennButton>
          </div>
          <ul className="divide-y divide-gray-100 rounded-2xl border border-gray-200">
          {items.map((f) => (
            <li
              key={f.fileId}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <PennText
                  as="p"
                  size="sm"
                  className="truncate font-medium text-gray-800"
                >
                  {f.fileName}
                </PennText>
                <PennText as="span" size="xs" color="blue_grey">
                  {formatSize(f.sizeBytes)}
                </PennText>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <PennButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void onDownload(f.fileId)}
                  disabled={downloadingId === f.fileId}
                >
                  {downloadingId === f.fileId ? "…" : "Download"}
                </PennButton>
                {!readOnly && (
                  <PennButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(f.fileId, f.fileName)}
                    disabled={remove.isPending}
                  >
                    Delete
                  </PennButton>
                )}
              </div>
            </li>
          ))}
          </ul>
        </>
      )}
    </div>
  );
};
